import React from "react";
import { displayAccountLabel } from "@common/helpers/accounting";
import {
    Picker,
    type PickerTreeRowPlacement,
} from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    EntityAvatarGroup,
    type EntityAvatarGroupEntity,
} from "@components/avatar/EntityAvatarGroup";
import {
    AccountGroupingIdentity,
    MultiEntityAccount,
} from "./multiEntityAccountGrouping";
import styles from "./MultiEntityAccountCombobox.module.scss";

interface Props extends PickerTreeRowPlacement {
    account: MultiEntityAccount;
    entities: EntityAvatarGroupEntity[];
    identity: AccountGroupingIdentity;
    selectedEntityIds?: number[];
    allowManuallyCreatedAcrossEntities?: boolean;
    showAccountNumbers: boolean;
    hideEntityIcons?: boolean;
    depth?: number;
}

export const MultiEntityAccountComboboxItem: React.FC<Props> = ({
    account,
    entities,
    identity,
    selectedEntityIds,
    allowManuallyCreatedAcrossEntities,
    showAccountNumbers,
    hideEntityIcons,
    depth = 0,
    treePosition,
    ancestorLines,
}) => {
    const isManuallyCreatedWithMultipleEntities =
        !allowManuallyCreatedAcrossEntities &&
        account.isManuallyCreated &&
        (selectedEntityIds ?? []).length > 1;

    const matchingEntities = entities.filter(
        (entity) =>
            account.entityIds.includes(entity.id) &&
            selectedEntityIds?.includes(entity.id),
    );

    const isPathIdentity = identity === "nameTypePath";
    const [firstCode, ...otherCodes] =
        isPathIdentity && showAccountNumbers ? account.codeTexts : [];

    const singleCodeLabel = isPathIdentity
        ? displayAccountLabel(
              { name: account.name, codeText: firstCode ?? null },
              showAccountNumbers,
          )
        : account.name;

    return (
        <Picker.Item
            value={account.key}
            disabled={isManuallyCreatedWithMultipleEntities}
            disabledReason={
                isManuallyCreatedWithMultipleEntities
                    ? "Manually created account is supported only for single entity condition"
                    : undefined
            }
            depth={depth}
            treePosition={treePosition}
            ancestorLines={ancestorLines}
            wrap
            trailing={
                hideEntityIcons ? undefined : (
                    <EntityAvatarGroup
                        entities={matchingEntities}
                        max={1}
                        size="xs"
                    />
                )
            }
        >
            {otherCodes.length > 0 ? (
                <>
                    {firstCode}{" "}
                    <Tooltip content={otherCodes.join(", ")}>
                        <span className={styles.extraCodesCount}>
                            [+{otherCodes.length}]
                        </span>
                    </Tooltip>
                    {" - "}
                    {account.name}
                </>
            ) : (
                singleCodeLabel
            )}
        </Picker.Item>
    );
};
